import { useState, useCallback, useRef, useEffect } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { Badge } from "../components/ui/badge"
import { Input } from "../components/ui/input"
import { cn } from "../lib/utils"

function coerceChips(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String)
  if (typeof raw === "string") {
    try { const parsed = JSON.parse(raw); if (Array.isArray(parsed)) return parsed.map(String) } catch { /* ignore */ }
  }
  return []
}

export function ChipInputRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()

  // Resolve bound chips
  const rawBound = element.bind
    ? resolveSymbol(element.bind.startsWith("#{") ? element.bind : `#{${element.bind}}`)
    : undefined
  const externalChips = rawBound != null ? coerceChips(rawBound) : undefined

  const initial = externalChips ?? ((element.options as any)?.chips ?? [])
  const [chips, setChips] = useState<string[]>(Array.isArray(initial) ? initial : [])
  const [inputValue, setInputValue] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)

  // Sync from external state
  useEffect(() => {
    if (externalChips !== undefined) {
      const ext = JSON.stringify(externalChips)
      const cur = JSON.stringify(chips)
      if (ext !== cur) setChips(externalChips)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawBound])

  const handleAdd = useCallback(() => {
    const val = inputValue.trim()
    if (val && !chips.includes(val)) {
      const next = [...chips, val]
      setChips(next)
      setInputValue("")
      dispatchEvent(element.id, "change", { chips: next })
    }
  }, [inputValue, chips, element.id, dispatchEvent])

  const handleRemove = useCallback(
    (chip: string) => {
      const next = chips.filter((c) => c !== chip)
      setChips(next)
      dispatchEvent(element.id, "change", { chips: next })
    },
    [chips, element.id, dispatchEvent],
  )

  return (
    <div
      className={cn("flex flex-col gap-2", element.className)}
      data-testid={`chip-input-${element.id}`}
    >
      <div className="flex flex-row flex-wrap gap-1.5">
        {chips.map((chip) => (
          <Badge
            key={chip}
            variant="secondary"
            className="gap-1"
          >
            {chip}
            <button
              type="button"
              className="cursor-pointer p-0 text-muted-foreground hover:text-foreground"
              onClick={() => handleRemove(chip)}
              aria-label={`Remove ${chip}`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </Badge>
        ))}
      </div>
      <Input
        ref={inputRef}
        type="text"
        placeholder={element.placeholder ?? "Add tag..."}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            handleAdd()
          }
        }}
        aria-label={element.ariaLabel ?? "Add new tag"}
      />
    </div>
  )
}
